import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  DynamicDialogConfig,
  DynamicDialogRef,
  DialogService,
} from "primeng/dynamicdialog";
import { SwalService } from "@shared/utils/swal.service";
import { MatDialog } from "@angular/material/dialog";
import { AbcEmpresasCustomizablesComponent } from "../../abc-empresas-customizables/abc-empresas-customizables.component";
import { AbcEmpresasPromocionesComponent } from "../abc-empresas-promociones/abc-empresas-promociones.component";
import { EmpresasService } from "../../../services/empresas.service";
import { SessionService } from "@shared/helpers/sesion.service";
import { ICiudad, ICreateSucursalReq } from "../../../interfaces/ISucursales";
import Swal from "sweetalert2";

@Component({
  selector: "app-abc-empresas-sucursales-form",
  templateUrl: "./abc-empresas-sucursales-form.component.html",
  styleUrls: ["./abc-empresas-sucursales-form.component.html"],
  providers: [DialogService],
})
export class AbcEmpresasSucursalesFormComponent implements OnInit {
  form: FormGroup;
  loading = false;
  modelRecived: any = null;
  listCiudades: ICiudad[] = [];
  isSecondaryModalOpen = false;

  constructor(
    private fb: FormBuilder,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private _swalService: SwalService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private empresasService: EmpresasService,
    private sessionService: SessionService
  ) {
    if (this.config.data) {
      this.modelRecived = this.config.data.sucursal;
    }
    this.initForm();
    if (this.modelRecived) {
      this.setFormData();
    }
  }

  ngOnInit(): void {
    this.getCiudades();
  }

  initForm(): void {
    const isEditMode = !!this.modelRecived;
    this.form = this.fb.group({
      claveEmpresa: [
        { value: this.config.data?.claveEmpresa || "", disabled: true },
      ],
      claveSucursal: [
        {
          value: "",
          disabled: isEditMode,
        },
        [Validators.required, Validators.maxLength(4), Validators.minLength(4)],
      ],
      nombreSucursal: ["", [Validators.required]],
      ciudad: ["", [Validators.required]],
    });

    // Convertir a mayúsculas la clave sucursal
    this.form.get("claveSucursal")?.valueChanges.subscribe((value) => {
      if (value) {
        const upperValue = value.toUpperCase();
        if (upperValue !== value) {
          this.form.patchValue(
            { claveSucursal: upperValue },
            { emitEvent: false }
          );
        }
      }
    });
  }

  setFormData(): void {
    if (this.modelRecived) {
      this.form.patchValue({
        claveEmpresa: this.config.data.claveEmpresa,
        claveSucursal: this.modelRecived.ssucursal,
        nombreSucursal: this.modelRecived.nombre_sucursal,
        ciudad: this.modelRecived.ciudad,
      });
    }
  }

  getCiudades(): void {
    this.loading = true;
    const sUsuario = this.sessionService.getUserSession();
    this.empresasService.GetSucursalCiudad(sUsuario).subscribe({
      next: (response) => {
        if (response.success) {
          this.listCiudades = response.data;
        } else {
          console.error("Error al cargar ciudades:", response);
          this._swalService.error("Error al cargar la lista de ciudades");
        }
        this.loading = false;
      },
      error: (error) => {
        console.error("Error al cargar ciudades:", error);
        this._swalService.error("Error al cargar la lista de ciudades");
        this.loading = false;
      },
    });
  }

  formValue(field: string) {
    return this.form.get(field);
  }

  create(formData: any): void {
    if (this.form.valid) {
      Swal.fire({
        title: "¿Está seguro?",
        text: "¿Desea crear esta sucursal?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, crear",
        cancelButtonText: "No",
        heightAuto: false,
        customClass: {
          container: "swal-index",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          const ciudadSeleccionada = this.listCiudades.find(ciudad => ciudad.claveCD === formData.ciudad);
          const payload: ICreateSucursalReq = {
            que: 1,
            clave: this.config.data.claveEmpresa,
            cveSucursal: formData.claveSucursal,
            nombreSucursal: formData.nombreSucursal,
            ciudad: ciudadSeleccionada?.claveCD || ""
          };

          this.empresasService.CreateSucursal(payload).subscribe({
            next: (response) => {
              if (response.success && response.data.error === 0) {
                this._swalService.success("Sucursal creada correctamente");
                this.ref.close(true);
              } else if (response.data.error === 1) {
                Swal.fire({
                  title: 'No es posible crear',
                  text: 'Ya existe una sucursal con esta clave',
                  icon: 'error',
                  confirmButtonText: 'Aceptar',
                  heightAuto: false,
                  customClass: {
                    container: 'swal-index',
                  },
                });
              } else {
                this._swalService.error("Error al crear la sucursal");
              }
              this.loading = false;
            },
            error: (error) => {
              console.error("Error al crear sucursal:", error);
              if (
                error.status === 500 && 
                (error.error?.details?.includes('duplicate') || error.error?.details?.includes('Violation of UNIQUE KEY'))
              ) {
                Swal.fire({
                  title: 'No es posible crear',
                  text: 'Ya existe una sucursal con esta clave',
                  icon: 'error',
                  confirmButtonText: 'Aceptar',
                  heightAuto: false,
                  customClass: {
                    container: 'swal-index',
                  },
                });
              } else {
                this._swalService.error("Error al crear la sucursal");
              }
              this.loading = false;
            },
          });
        }
      });
    }
  }

  update(formData: any): void {
    if (this.form.valid && !this.isSecondaryModalOpen) {
      Swal.fire({
        title: "¿Está seguro?",
        text: "¿Desea actualizar esta sucursal?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        cancelButtonText: "No",
        heightAuto: false,
        customClass: {
          container: "swal-index",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.loading = true;
          const ciudadSeleccionada = this.listCiudades.find(ciudad => ciudad.claveCD === formData.ciudad);
          const payload: ICreateSucursalReq = {
            que: 2,
            clave: this.config.data.claveEmpresa,
            cveSucursal: formData.claveSucursal,
            nombreSucursal: formData.nombreSucursal,
            ciudad: ciudadSeleccionada?.claveCD || ""
          };

          this.empresasService.CreateSucursal(payload).subscribe({
            next: (response) => {
              if (response.success && response.data.error === 0) {
                this._swalService.success("Sucursal actualizada correctamente");
                this.ref.close(true);
              } else {
                this._swalService.error("Error al actualizar la sucursal");
              }
              this.loading = false;
            },
            error: (error) => {
              console.error("Error al actualizar sucursal:", error);
              if (
                error.status === 500 &&
                error.error?.message?.includes("duplicate")
              ) {
                this._swalService.error(
                  "No es posible duplicar sucursales en la misma ciudad"
                );
              } else {
                this._swalService.error("Error al actualizar la sucursal");
              }
              this.loading = false;
            },
          });
        }
      });
    }
  }

  customizables(): void {
    this.isSecondaryModalOpen = true;
    const formState = {
      dirty: this.form.dirty,
      pristine: this.form.pristine,
      touched: this.form.touched
    };

    this.form.markAsPristine();
    this.form.markAsUntouched();

    // Agregar clase al contenedor de overlay antes de abrir el diálogo
    document.querySelector('.cdk-overlay-container')?.classList.add('customizables-active');

    const dialogRef = this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: "1000px",
      height: "1000px",
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: "sucursales",
        sClave: `${this.config.data.claveEmpresa}-${this.form.get("claveSucursal").value}`,
        sNombre: this.form.get("nombreSucursal").value,
        sTipo: "Sucursal"
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Remover clase del contenedor de overlay después de cerrar
      document.querySelector('.cdk-overlay-container')?.classList.remove('customizables-active');
      
      this.isSecondaryModalOpen = false;
      if (formState.dirty) {
        this.form.markAsDirty();
      }
      if (formState.touched) {
        this.form.markAsTouched();
      }
    });
  }

  promociones(): void {
    this.isSecondaryModalOpen = true;
    const formState = {
      dirty: this.form.dirty,
      pristine: this.form.pristine,
      touched: this.form.touched,
    };

    this.form.markAsPristine();
    this.form.markAsUntouched();

    const ref = this.dialogService.open(AbcEmpresasPromocionesComponent, {
      header: "Promociones",
      width: "80%",
      height: "100%",
      appendTo: "body",
      data: {
        empresa: this.config.data.empresa,
        claveEmpresa: this.config.data.claveEmpresa,
        claveSucursal: this.form.get("claveSucursal").value,
        nombreSucursal: this.form.get("nombreSucursal").value,
      },
    });

    ref.onClose.subscribe(() => {
      this.isSecondaryModalOpen = false;
      if (formState.dirty) {
        this.form.markAsDirty();
      }
      if (formState.touched) {
        this.form.markAsTouched();
      }
    });
  }

  close(): void {
    this.ref.close();
  }
}
